import type { ComponentType } from "react";
import ComponentTypes from "~/features/cms/componentTypes";
import type { CMSComponentProps } from "~/features/cms/types/props";

// Components
import { CMSSection } from "~/features/cms/components/cmsSection/CMSSection";
import { CMSButton } from "~/features/cms/components/cmsButton/CMSButton";
import { CMSLink } from "~/features/cms/components/cmsLink/CMSLink";
import { CMSList } from "~/features/cms/components/cmsList/CMSList";
import { CMSStandaloneImage } from "~/features/cms/components/cmsMedia/CMSStandaloneMedia";
import { CMSTeasers } from "~/features/cms/components/cmsTeaser/CMSTeasers";
import CMSTeaser from "~/features/cms/components/cmsTeaser/CMSTeaser";
import { CMSBanner } from "~/features/cms/components/cmsBanner/CMSBanner";
import { CMSRotatingBanners } from "~/features/cms/components/cmsBanner/CMSRotatingBanners";
import { CMSFilterableTileContainer } from "~/features/cms/components/cmsFilterableTile/CMSFilterableTileContainer";
import { CMSLoginCall } from "~/features/cms/components/cmsLogin/CMSLoginCall";
import { CMSQuote } from "~/features/cms/components/cmsQuote/CMSQuote";
import { CMSColumnContainer } from "~/features/cms/components/cmsColumn/CMSColumnContainer";
import { CMSColumn } from "~/features/cms/components/cmsColumn/CMSColumn";
import { CMSAccordionContainer } from "~/features/cms/components/cmsAccordionContainer/CMSAccordionContainer";
import { CMSAccordion } from "~/features/cms/components/cmsAccordion/CMSAccordion";
import { CMSPageNavigation } from "~/features/cms/components/cmsPageNavigation/CMSPageNavigation";
import { CMSNewsletterConsent } from "~/features/cms/components/cmsNewsletter/CMSNewsletterConsent";
import { CMSPhoto } from "~/features/cms/components/cmsImage/CMSImage";
import { CMSPhotoGallery } from "~/features/cms/components/cmsImage/CMSPhotoGallery";
import { CMSColumnLinks } from "~/features/cms/components/cmsColumn/CMSColumnLinks";
import { EwellixPageNavigationComponent } from "~/features/cms/components/cmsPageNavigation/EwellixPageNavigationComponent";
import { CMSTable } from "~/features/cms/components/cmsTable/CMSTable";
import { SchaefflerForm } from "~/features/cms/schaefflerForm/SchaefflerForm";
import { SchaefflerFormHiddenField } from "~/features/cms/schaefflerForm/SchaefflerFormHiddenField";
import { SchaefflerFormSelectField } from "~/features/cms/schaefflerForm/SchaefflerFormSelectField";
import { SchaefflerFormTextField } from "~/features/cms/schaefflerForm/SchaefflerFormTextField";
import { SchaefflerFormCheckboxField } from "~/features/cms/schaefflerForm/SchaefflerFormCheckboxField";
import { CMSCardComponent } from "~/features/cms/components/cmsCard/CMSCardComponent";
import { CMSHomepagePersonalization } from "~/features/cms/homepagePersonalization/CMSHomepagePersonalization";
import { CMSChipLink } from "~/features/cms/components/cmsChip/CMSChipLink";
import { CMSChipList } from "~/features/cms/components/cmsChip/CMSChipList";
import { CMSParagraph } from "~/features/cms/components/cmsParagraph/CMSParagraph";
import CMSKameleoonSlot from "~/features/cms/components/cmsKameleoon/CMSKameleoonSlot";

const COMPONENT_MAP: {
  [K in CMSComponentProps["type"]]: ComponentType<Extract<CMSComponentProps, { type: K }>>;
} = {
  [ComponentTypes.CMSSection]: CMSSection,
  [ComponentTypes.CMSStandaloneMedia]: CMSStandaloneImage,
  [ComponentTypes.CMSList]: CMSList,
  [ComponentTypes.CMSButton]: CMSButton,
  [ComponentTypes.CMSLink]: CMSLink,
  [ComponentTypes.CMSTeaserContainer]: CMSTeasers,
  [ComponentTypes.CMSTeaser]: CMSTeaser,
  [ComponentTypes.CMSPublicationTeaser]: CMSTeaser,
  [ComponentTypes.CMSBanner]: CMSBanner,
  [ComponentTypes.CMSRotatingBanners]: CMSRotatingBanners,
  [ComponentTypes.CMSFilterableTileContainer]: CMSFilterableTileContainer,
  [ComponentTypes.CMSParagraph]: CMSParagraph,
  [ComponentTypes.CMSLoginCall]: CMSLoginCall,
  [ComponentTypes.CMSQuote]: CMSQuote,
  [ComponentTypes.CMSColumnsContainer]: CMSColumnContainer,
  [ComponentTypes.CMSColumn]: CMSColumn,
  [ComponentTypes.CMSAccordionContainer]: CMSAccordionContainer,
  [ComponentTypes.CMSAccordion]: CMSAccordion,
  [ComponentTypes.CMSTable]: CMSTable,
  [ComponentTypes.CMSPageNavigation]: CMSPageNavigation,
  [ComponentTypes.CMSNewsLetterConsent]: CMSNewsletterConsent,
  [ComponentTypes.CMSPhotoGallery]: CMSPhotoGallery,
  [ComponentTypes.CMSPhoto]: CMSPhoto,
  [ComponentTypes.CMSColumnLinks]: CMSColumnLinks,
  [ComponentTypes.EwellixPageNavigation]: EwellixPageNavigationComponent,
  [ComponentTypes.Form]: SchaefflerForm,
  [ComponentTypes.FormHiddenField]: SchaefflerFormHiddenField,
  [ComponentTypes.FormFieldSelect]: SchaefflerFormSelectField,
  [ComponentTypes.FormFieldText]: SchaefflerFormTextField,
  [ComponentTypes.FormFieldCheckbox]: SchaefflerFormCheckboxField,
  [ComponentTypes.CMSCardComponent]: CMSCardComponent,
  [ComponentTypes.CMSHomepagePersonalization]: CMSHomepagePersonalization,
  [ComponentTypes.CMSChipLink]: CMSChipLink,
  [ComponentTypes.CMSChipList]: CMSChipList,
  [ComponentTypes.CMSKameleoonSlot]: CMSKameleoonSlot,
} as const;

export const renderCMSComponent = (props: CMSComponentProps, className?: string, key?: number): null | JSX.Element => {
  const Component = COMPONENT_MAP[props.type];

  if (!Component) {
    console.error(`Could not find a component for type ${props.type}`);
    return null;
  }

  // @ts-ignore
  return <Component key={key} className={className} {...props} />;
};
